import React from 'react';
import { useNavigate } from 'react-router-dom';
import LockIcon from '@mui/icons-material/Lock';
import { useWhatsAppSender } from '../../contexts/WhatsAppSenderContext';
import useSubscriptionFeatures from '../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';
import styles from './WhatsAppOriginBanner.module.css';

const CAPABILITY_BY_CONTEXT = {
  bulk: 'bulk',
  template: 'template_edit',
  document: 'document',
  chat: 'manual_chat',
};

// "ação" usada nas mensagens, uma por contexto (frase completa e forma reduzida/minúscula).
const ACTION_TEXT = {
  bulk: { sentence: 'O envio em massa', lower: 'fazer o envio em massa' },
  template: { sentence: 'O envio com mensagens personalizadas', lower: 'personalizar as mensagens' },
  document: { sentence: 'O envio de documentos', lower: 'enviar documentos' },
  chat: { sentence: 'O chat', lower: 'usar o chat' },
};

/**
 * Banner inline que explica por que uma ação de WhatsApp não está disponível, de acordo com a
 * origem de envio atual da empresa (número da Gabriela x número próprio). Baseado visualmente em
 * `FeatureLockBanner` (src/components/subscription/FeatureLockBanner.js).
 *
 * Renderiza `null` enquanto o status ainda está carregando, ou quando a capability
 * correspondente ao `context` já está liberada (nada para avisar).
 *
 * Um cadeado só: se o plano não inclui número próprio, fala do plano e leva para a
 * assinatura; se inclui e o número não está ativo, fala do número e leva para
 * Configurações. Frase de produto (desde 2026-10-23 nada ao paciente sai pelo
 * número da Gabriela): sem número próprio as mensagens saem pelo WhatsApp Web
 * com o texto pronto; pelo seu WhatsApp sai tudo pelo sistema
 * (docs/features/subscription/matriz-de-planos.md).
 *
 * @param {Object} props
 * @param {'bulk'|'template'|'document'|'chat'} props.context
 * @param {string} [props.className]
 */
const WhatsAppOriginBanner = ({ context, className = '' }) => {
  const navigate = useNavigate();
  const { loading, state, capabilities } = useWhatsAppSender();
  const { hasPermission } = useSubscriptionFeatures();

  const capabilityKey = CAPABILITY_BY_CONTEXT[context];

  if (loading || !capabilityKey || capabilities[capabilityKey]) {
    return null;
  }

  const action = ACTION_TEXT[context];
  const planAllowsOwnNumber = hasPermission(SUBSCRIPTION_PERMISSIONS.ZAPI_INTEGRATION);

  const getMessage = () => {
    if (!planAllowsOwnNumber) {
      return (
        <>
          Neste plano as mensagens saem pelo WhatsApp Web, com o texto pronto.{' '}
          {action.sentence} sai pelo seu próprio número de WhatsApp, que está disponível a partir
          do plano Profissional.
        </>
      );
    }

    if (state === 'own_disabled') {
      return (
        <>
          Seu número próprio está desligado. Ative &quot;Usar meu próprio número&quot; em
          Integrações › WhatsApp para {action.lower}.
        </>
      );
    }

    if (state === 'own_unavailable') {
      return (
        <>
          Seu número de WhatsApp está desconectado. Reconecte em Integrações › WhatsApp para{' '}
          {action.lower}.
        </>
      );
    }

    // state === 'platform' (sem número próprio configurado)
    return (
      <>
        Sem número próprio, as mensagens saem pelo WhatsApp Web, com o texto pronto.{' '}
        {action.sentence} sai pelo seu WhatsApp: conecte seu número em Integrações › WhatsApp
        para {action.lower}.
      </>
    );
  };

  const buttonLabel = planAllowsOwnNumber ? 'Configurar WhatsApp' : 'Ver planos';
  const buttonTarget = planAllowsOwnNumber ? '/company/whatsapp' : '/subscription/plans';

  return (
    <div className={`${styles.banner} ${className}`} role="alert">
      <div className={styles.content}>
        <LockIcon className={styles.icon} fontSize="small" />
        <span className={styles.message}>{getMessage()}</span>
      </div>
      <button
        className={styles.upgradeButton}
        onClick={() => navigate(buttonTarget)}
        type="button"
      >
        {buttonLabel}
      </button>
    </div>
  );
};

export default WhatsAppOriginBanner;
